// Modal name constants (defined here to avoid circular dependency)
export const ModalName = {
  Dialer: 'dialer',
  Settings: 'settings',
  Logs: 'logs',
  AgentsList: 'agents',
  DTMF: 'dtmf',
  EmailPreview: 'email-preview',
} as const;

export type ModalNameType = (typeof ModalName)[keyof typeof ModalName];

// Discriminated union types for each modal
type DialerModalParams = {modal: typeof ModalName.Dialer; to?: string};
type SettingsModalParams = {modal: typeof ModalName.Settings};
type LogsModalParams = {modal: typeof ModalName.Logs};
type AgentsModalParams = {modal: typeof ModalName.AgentsList; excludeIdentity?: string};
type DTMFModalParams = {modal: typeof ModalName.DTMF};
type EmailPreviewModalParams = {modal: typeof ModalName.EmailPreview; emlUrl: string; subject?: string};

export type ModalParams =
  | DialerModalParams
  | SettingsModalParams
  | LogsModalParams
  | AgentsModalParams
  | DTMFModalParams
  | EmailPreviewModalParams;

/**
 * Build modal params as URLSearchParams.
 * All non-modal params are prefixed with m_ unconditionally.
 */
const buildModalSearchParams = <T extends ModalParams>(params: T): URLSearchParams => {
  const searchParams = new URLSearchParams();
  searchParams.set('modal', params.modal);

  for (const [key, value] of Object.entries(params)) {
    if (key !== 'modal' && value !== undefined && value !== '') {
      searchParams.set(`m_${key}`, String(value));
    }
  }

  return searchParams;
};

/**
 * Merge path params with modal params into a full URL.
 */
const mergeParams = (basePath: string, modalParams: ModalParams): string => {
  const [pathname, existingSearch] = basePath.split('?');
  const searchParams = new URLSearchParams(existingSearch || '');

  const modalSearchParams = buildModalSearchParams(modalParams);
  for (const [key, value] of modalSearchParams.entries()) {
    searchParams.set(key, value);
  }

  const queryString = searchParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * Build modal URL by merging modal params into current path.
 */
export const buildModalUrl = (currentPath: string, modalParams: ModalParams): string => {
  return mergeParams(currentPath, modalParams);
};

/**
 * Build full URL with typed route params and optional modal params.
 */
export const buildFullUrl = <P>(
  route: {$buildPath: (params: P) => string},
  params: P,
  modalParams?: ModalParams,
): string => {
  const fullPath = route.$buildPath(params);
  if (!modalParams) return fullPath;
  return mergeParams(fullPath, modalParams);
};

